'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { 
  FiDatabase, FiRefreshCw, FiDatabase as FiDbIcon, 
  FiEye, FiCopy, FiLock, FiInfo 
} from 'react-icons/fi'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/ui-dialog"
import { toast } from 'react-hot-toast'
import { cn } from '@/lib/utils'

const CredentialItem = ({ label, value, isSecret = false }: { label: string, value: string, isSecret?: boolean }) => {
  const [show, setShow] = useState(!isSecret)
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copiado`)
  }

  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{label}</label>
      <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg h-10 px-3 group">
        <span className="text-sm font-mono text-gray-700 truncate">
          {isSecret && !show ? '••••••••' : value}
        </span>
        <div className="flex items-center gap-1">
          {isSecret && (
            <button 
              onClick={() => setShow(!show)}
              className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
            >
              <FiEye className="h-3.5 w-3.5" />
            </button>
          )}
          <button 
            onClick={() => copyToClipboard(value)}
            className="p-1.5 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-all translate-x-1 group-hover:translate-x-0"
          >
            <FiCopy className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default function DatabasesPage() {
  const [tables, setTables] = useState<any[]>([])
  const [account, setAccount] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  
  // Data view state
  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [tableData, setTableData] = useState<any[]>([])
  const [loadingData, setLoadingData] = useState(false)

  useEffect(() => {
    fetchUserData()
    
    // Escuchar cambios de cuenta desde el Navbar
    const handleStorageChange = () => fetchUserData()
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const fetchUserData = async () => {
    try {
      const res = await fetch('/api/auth/verificar')
      const data = await res.json()
      if (data.success) {
        // Buscar la cuenta seleccionada en localStorage
        const savedAccId = localStorage.getItem('selected_hosting_account')
        const accounts = data.usuario.hostingAccounts || []
        const acc = accounts.find((a: any) => a.id.toString() === savedAccId) || accounts[0]
        
        if (acc) {
          setAccount(acc)
          fetchTables(acc.dbName)
        } else {
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    } catch (error) {
      console.error('Error fetching user data:', error)
      setLoading(false)
    }
  }

  const fetchTables = async (dbName: string) => {
    setLoading(true)
    try {
      const res = await fetch(`/api/hosting/databases?dbName=${dbName}`)
      const data = await res.json()
      setTables(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching tables:', error)
    }
    setLoading(false)
  }

  const viewTableData = async (tableName: string) => {
    setSelectedTable(tableName)
    setLoadingData(true)
    try {
      const res = await fetch(`/api/hosting/databases/records?dbName=${account.dbName}&tableName=${tableName}`)
      const data = await res.json()
      setTableData(data.success ? data.data : [])
    } catch (error) {
      toast.error('Error al cargar datos')
    }
    setLoadingData(false)
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Bases de Datos</h1>
          <p className="text-gray-500 text-sm">Visualiza y gestiona las tablas de tu hosting.</p>
        </div>
        <Button onClick={() => account?.dbName && fetchTables(account.dbName)} variant="outline" className="gap-2 bg-white">
          <FiRefreshCw className={loading ? 'animate-spin' : ''} /> Actualizar
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-1">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-none shadow-sm">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="rounded-xl bg-blue-50 p-3 text-blue-500">
                <FiDbIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Tablas</p>
                <p className="text-xl font-bold">{tables.length}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-sm md:col-span-2 overflow-hidden">
            <CardHeader className="py-3 bg-gray-50/50 border-b flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FiLock className="text-amber-500" /> Credenciales para conexión remota
              </CardTitle>
              <div className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-bold uppercase">Externo</div>
            </CardHeader>
            <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-4">
              <CredentialItem label="SERVIDOR (HOST)" value="miapi.cloud" />
              <CredentialItem label="USUARIO" value={account?.dbUser || account?.dbName || '---'} />
              <CredentialItem label="CONTRASEÑA" value={account?.dbPass || '••••••••'} isSecret />
              <CredentialItem label="BASE DE DATOS" value={account?.dbName || '---'} />
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="overflow-hidden border-none shadow-sm">
        <CardHeader className="bg-gray-50/50 border-b">
          <CardTitle className="text-lg flex items-center gap-2">
            <FiDatabase className="text-blue-500" /> Tablas en <span className="text-blue-600 font-mono">{account?.dbName}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table 
            className="border-separate border-spacing-0" 
            wrapperClassName="max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200"
          >
            <TableHeader className="sticky top-0 z-40 bg-gray-50">
              <TableRow>
                <TableHead className="sticky top-0 z-40 bg-gray-50 font-bold text-gray-700 border-b shadow-[0_1px_0_0_rgba(0,0,0,0.1)]">Nombre de Tabla</TableHead>
                <TableHead className="sticky top-0 z-40 bg-gray-50 font-bold text-gray-700 border-b shadow-[0_1px_0_0_rgba(0,0,0,0.1)]">Registros</TableHead>
                <TableHead className="sticky top-0 z-40 bg-gray-50 font-bold text-gray-700 border-b shadow-[0_1px_0_0_rgba(0,0,0,0.1)]">Tamaño (MB)</TableHead>
                <TableHead className="sticky top-0 z-40 bg-gray-50 font-bold text-gray-700 border-b shadow-[0_1px_0_0_rgba(0,0,0,0.1)]">Motor</TableHead>
                <TableHead className="sticky top-0 z-40 bg-gray-50 font-bold text-gray-700 text-right border-b shadow-[0_1px_0_0_rgba(0,0,0,0.1)]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-20">
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                      <p className="text-sm text-gray-500">Consultando base de datos...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                tables.map((table, i) => (
                  <TableRow key={i} className="hover:bg-gray-50/50 transition-colors group">
                    <TableCell className="font-medium font-mono text-sm">{table.name}</TableCell>
                    <TableCell className="text-gray-600 font-medium">{Number(table.rows).toLocaleString()}</TableCell>
                    <TableCell className="text-gray-600">{Number(table.size).toFixed(3)}</TableCell>
                    <TableCell>
                      <span className="px-2 py-1 rounded text-[10px] font-bold bg-gray-100 text-gray-600 uppercase">
                        {table.engine}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => viewTableData(table.name)}
                        className="h-8 gap-2 text-blue-600 hover:bg-blue-50 hover:text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FiEye className="h-4 w-4" /> Ver Datos
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {tables.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-20 text-gray-500">
                    <div className="flex flex-col items-center gap-2 opacity-20">
                      <FiDbIcon className="h-12 w-12" />
                      <p>No se encontraron tablas en esta base de datos.</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* MODAL VER DATOS */}
      <Dialog open={!!selectedTable} onOpenChange={(open) => !open && setSelectedTable(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0 overflow-hidden border-none shadow-2xl">
          <DialogHeader className="p-6 bg-blue-600 text-white">
            <DialogTitle className="flex items-center gap-3 text-xl">
              <FiDatabase className="h-5 w-5" />
              <span>Registros de la tabla: <strong className="font-mono text-blue-100">{selectedTable}</strong></span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto p-0 min-h-[300px]">
             {loadingData ? (
               <div className="h-full flex flex-col items-center justify-center py-20 gap-3">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
                  <p className="text-gray-500 animate-pulse">Cargando datos...</p>
               </div>
             ) : tableData.length > 0 ? (
               <div className="relative overflow-x-auto">
                 <table className="w-full text-sm text-left border-collapse">
                    <thead className="text-xs uppercase bg-gray-100 text-gray-600 sticky top-0 z-10 shadow-sm">
                      <tr>
                        {Object.keys(tableData[0]).map(key => (
                          <th key={key} className="px-4 py-3 border-b font-bold whitespace-nowrap">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50 transition-colors">
                          {Object.values(row).map((val: any, i) => (
                            <td key={i} className="px-4 py-2 text-gray-700 whitespace-nowrap max-w-[300px] truncate">
                              {val === null ? <span className="text-gray-300 italic">null</span> : String(val)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                 </table>
               </div>
             ) : (
               <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-2">
                  <FiInfo className="h-10 w-10" />
                  <p>La tabla no tiene registros o no se pudieron cargar.</p>
               </div>
             )}
          </div>
          
          <div className="p-4 bg-gray-50 flex justify-between items-center border-t">
            <span className="text-xs text-gray-500">Mostrando hasta los últimos 100 registros</span>
            <Button variant="outline" onClick={() => setSelectedTable(null)}>Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
